import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Select, Table, Tag, Typography } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';

import { useAccess } from '../../../auth/AccessContext.jsx';
import ActionIcon from '../../../components/ActionIcon.jsx';
import PriceFees from '../../../components/PriceFees.jsx';
import StatusTag from '../../../components/StatusTag.jsx';
import { useLiveTopic } from '../../../live/LiveContext.js';
import { formatInstant, formatMoney, formatUnits } from '../../../format';
import { STATUS_KIND } from '../../../status/statusHelp.js';
import { useStepUp } from '../../../stepup/useStepUp.js';
import { usePagedList } from '../registry/usePagedList.js';
import RunDrawer from './RunDrawer.jsx';
import {
    SOURCE_LABEL, STATE_COLOUR, dayOnDay, latestFileRuns, priceCards, pricesQuery, runOffers, runsQuery
} from './runs.js';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const dash = (value, format = (v) => v) => (value === null || value === undefined || value === '' ? '—' : format(value));
const day = (range, i) => range?.[i]?.format('YYYY-MM-DD') ?? null;

/** A panel's hint, in the gold info banner the Console's refreshed panels share. */
const InfoBanner = ({ children }) => (
    <div className="unit-prices-info">
        <ActionIcon name="info" />
        <span>{children}</span>
    </div>
);

/**
 * The current price cards (runs.js priceCards): one per ACTIVE fund, its
 * latest published NAV per unit, selling, buying and buying-with-exit-fee
 * prices and the dealing date. Where a price came from is left to Price
 * History and the run drawer (Figma "Card 1" has no source).
 * Rendered by UnitPrices, and on its own by UnitPrices.test.js.
 */
export const PriceCards = ({ cards }) => (
    <div className="price-card-grid">
        {cards.map((card) => (
            <div className="price-card" key={card.fundId}>
                <div className="price-card-body">
                    <div className="price-card-code">{card.fundId}</div>
                    <div className="price-card-name">{card.name}</div>
                    <div className="price-card-figure">
                        <div className="price-card-nav">{card.price ? formatUnits(card.price.navPerUnit) : '—'}</div>
                        <div className="price-card-caption">
                            {card.price ? 'NAV per unit (LKR), ' + card.price.dealingDate : 'No published price yet'}
                        </div>
                    </div>
                    {card.price && (
                        <div className="price-card-prices">
                            <dl className="price-card-pair">
                                <div><dt>Selling</dt><dd className="is-selling">{dash(card.price.sellingPrice, formatUnits)}</dd></div>
                                <div><dt>Buying</dt><dd className="is-buying">{dash(card.price.buyingPrice, formatUnits)}</dd></div>
                            </dl>
                            <PriceFees price={card.price} />
                        </div>
                    )}
                </div>
            </div>
        ))}
    </div>
);

/** Stops a button's click from also opening the row's drawer. */
const own = (fn) => (e) => {
    e.stopPropagation();
    fn();
};

/**
 * NAV runs as GET pricing/runs answers them: the fund (named from fundNames,
 * the screen's own read of the funds), its NAV, the units in
 * issue and the NAV per unit against the prior day's, and the decisions this
 * person may take on the row (runs.js runOffers; a run's checks are in its
 * drawer) - each opens the run's drawer and starts
 * there, so the step-up is asked for over the run's full detail. A run with
 * nothing to decide shows its state. Selecting a row opens its drawer.
 * Rendered by UnitPrices, and on its own by UnitPrices.test.js.
 */
export const RunsTable = ({ runs, fundNames = {}, loading = false, onOpen, onDecide }) => {
    const { canAct, principal } = useAccess();
    const who = {
        uid: principal?.uid,
        canOverride: false,
        canApprove: canAct('unitPrices', 'approve'),
        canReject: canAct('unitPrices', 'reject')
    };
    const decide = (r, intent) => own(() => (onDecide ?? onOpen)(r.navRunId, intent));
    const columns = [
        {
            title: 'Fund',
            dataIndex: 'fundId',
            key: 'fundId',
            width: 260,
            className: 'run-col-fund',
            render: (v, r) => (
                <div className="run-fund" title={'Valuation date ' + r.valuationDate}>
                    <div className="run-fund-code">{v}{r.version > 1 && <Text type="secondary">{' v' + r.version}</Text>}</div>
                    {fundNames[v] && <div className="run-fund-name">{fundNames[v]}</div>}
                </div>
            )
        },
        { title: 'NAV Value (LKR)', dataIndex: 'nav', key: 'nav', width: 200, className: 'run-col-num', render: (v) => dash(v, formatMoney) },
        {
            title: 'Issued Unit Count', dataIndex: 'unitsInIssue', key: 'unitsInIssue', width: 200, className: 'run-col-num',
            render: (v) => dash(v, formatUnits)
        },
        {
            title: 'Unit Price (LKR)',
            key: 'navPerUnit',
            width: 200,
            className: 'run-col-price',
            render: (_, r) => (
                <div className="run-price" title={'Day on day ' + dayOnDay(r)}>
                    <div className="run-price-now">{dash(r.navPerUnit, formatUnits)}</div>
                    {r.priorNavPerUnit && <div className="run-price-was">{'was ' + formatUnits(r.priorNavPerUnit)}</div>}
                </div>
            )
        },
        {
            title: '',
            key: 'decide',
            width: 260,
            className: 'run-col-actions',
            render: (_, r) => {
                const offers = runOffers(r, who);
                const actions = [];
                if (offers.approve.offered) {
                    actions.push(<Button key="approve" size="small" type="primary" className="run-approve" disabled={Boolean(offers.approve.blocked)}
                        onClick={decide(r, 'approve')}>Approve</Button>);
                }
                if (offers.reject.offered) {
                    actions.push(<Button key="reject" size="small" danger className="run-reject" onClick={decide(r, 'reject')}>Reject</Button>);
                }
                return (
                    <div className="run-actions">
                        {r.state !== 'CHECKED' && <StatusTag kind={STATUS_KIND.NAV_RUN} code={r.state} color={STATE_COLOUR[r.state] ?? 'default'} />}
                        {actions}
                    </div>
                );
            }
        }
    ];
    return (
        <Table rowKey="navRunId" className="unit-prices-table runs-table" loading={loading} columns={columns} dataSource={runs} pagination={false}
            tableLayout="fixed" scroll={{ x: 1120 }} locale={{ emptyText: 'No NAV runs match' }}
            onRow={(r) => ({ onClick: () => onOpen(r.navRunId), style: { cursor: 'pointer' } })} />
    );
};

/**
 * Published prices as GET pricing/prices answers them, newest dealing date
 * first: the four prices, their source, and when each was published (and
 * superseded, when the superseded versions are shown). Rendered by
 * UnitPrices, and on its own by UnitPrices.test.js.
 */
export const PricesTable = ({ prices, loading = false }) => {
    const columns = [
        { title: 'Effective Date', dataIndex: 'dealingDate', key: 'dealingDate', width: 130 },
        { title: 'Fund', dataIndex: 'fundId', key: 'fundId', width: 90, render: (v, p) => <>{v}{p.version > 1 && <Text type="secondary">{' v' + p.version}</Text>}</> },
        { title: 'NAV Per Unit', dataIndex: 'navPerUnit', key: 'navPerUnit', render: (v) => dash(v, formatUnits) },
        { title: 'Selling', dataIndex: 'sellingPrice', key: 'sellingPrice', render: (v) => dash(v, formatUnits) },
        { title: 'Buying', dataIndex: 'buyingPrice', key: 'buyingPrice', render: (v) => dash(v, formatUnits) },
        { title: 'Buying with exit fee', dataIndex: 'buyingPriceWithExitFee', key: 'bwef', render: (v) => dash(v, formatUnits) },
        { title: 'Selling with front-end fee', dataIndex: 'sellingPriceWithEntryFee', key: 'swef', render: (v) => dash(v, formatUnits) },
        { title: 'Source', dataIndex: 'source', key: 'source', render: (v) => SOURCE_LABEL[v] ?? dash(v) },
        {
            title: 'Published',
            key: 'published',
            width: 180,
            render: (_, p) => <>{formatInstant(p.publishedAt)}{p.supersededAt && <div><Tag>Superseded {formatInstant(p.supersededAt)}</Tag></div>}</>
        }
    ];
    return (
        <Table rowKey="priceId" className="unit-prices-table" loading={loading} columns={columns} dataSource={prices} pagination={false} scroll={{ x: 900 }}
            locale={{ emptyText: 'No published prices match' }} />
    );
};

/**
 * Unit Prices (IP-61, IP-62, IP-105, IP-106, IP-111, IP-112; design
 * 2026-10-02 section 8), on the Console API. The Unit Registry tab bar no
 * longer mounts this screen: Fund Register (UnitRegistry) hosts the runs
 * and the price history. These tables and cards stay here for that screen.
 *
 *   - The current price cards: GET register/funds, the ACTIVE funds' latest
 *     published prices and their source.
 *   - NAV runs: GET pricing/runs?batch=latest, the runs of the last file
 *     uploaded, titled with its file name, a page at a time; a run's drawer (RunDrawer) shows its checks, the file
 *     it came from and its trail, and offers Override, Approve and Reject,
 *     each on a fresh authenticator code (the screen's one step-up
 *     dialog). A row's Approve and Reject open the drawer with that
 *     decision started (RunDrawer intent).
 *   - Price history: GET pricing/prices, filtered by fund and dealing date,
 *     superseded versions on request, a page at a time.
 *
 * A decision reads the cards and both lists again, and tells the Unit
 * Registry's tab badge (onRunsChanged), which counts the runs waiting for
 * this person.
 */
const UnitPrices = ({ onRunsChanged }) => {
    const { api } = useAccess();
    const { withStepUp, stepUpDialog } = useStepUp();

    const [funds, setFunds] = useState([]);
    const [fundsLoading, setFundsLoading] = useState(false);
    const [fundsError, setFundsError] = useState(null);
    const [refreshedAt, setRefreshedAt] = useState(null);
    // The run whose drawer is open, and the decision a row's button started there (or null).
    const [openRun, setOpenRun] = useState(null);

    // The file the runs shown came from (GET pricing/runs?batch=latest names it), or null before it is read / when none.
    const [runFile, setRunFile] = useState(null);
    const [priceFund, setPriceFund] = useState(null);
    const [priceRange, setPriceRange] = useState(null);
    const [superseded, setSuperseded] = useState(false);

    const loadFunds = useCallback(async () => {
        setFundsLoading(true);
        setFundsError(null);
        try {
            const answer = await api.register.funds();
            setFunds(answer.funds ?? []);
            setRefreshedAt(new Date());
        } catch (e) {
            setFundsError(e.message);
        } finally {
            setFundsLoading(false);
        }
    }, [api]);

    useEffect(() => {
        loadFunds();
    }, [loadFunds]);

    const fetchRuns = useCallback(async (cursor) => {
        const page = await api.pricing.runs(runsQuery({ batch: 'latest', cursor }));
        if (page.batch !== undefined) {
            if (!cursor) setRunFile(page.batch);
            return { items: page.runs ?? [], nextCursor: page.nextCursor ?? null };
        }
        // An API from before ?batch= ignores it and lists every run: keep the newest run's file here instead (runs.js latestFileRuns).
        const runsOfFile = latestFileRuns(page.runs);
        let fileName = null;
        if (runsOfFile.length) {
            try {
                fileName = (await api.pricing.run(runsOfFile[0].navRunId)).batch?.fileName ?? null;
            } catch {
                fileName = null;
            }
        }
        setRunFile(runsOfFile.length ? { batchId: runsOfFile[0].batchId, fileName, businessDate: runsOfFile[0].valuationDate } : null);
        return { items: runsOfFile, nextCursor: null };
    }, [api]);
    const runs = usePagedList(fetchRuns, 'navRunId');

    const priceFrom = day(priceRange, 0);
    const priceTo = day(priceRange, 1);
    const fetchPrices = useCallback(async (cursor) => {
        const page = await api.pricing.prices(pricesQuery({ fundId: priceFund, from: priceFrom, to: priceTo, includeSuperseded: superseded, cursor }));
        return { items: page.prices ?? [], nextCursor: page.nextCursor ?? null };
    }, [api, priceFund, priceFrom, priceTo, superseded]);
    const prices = usePagedList(fetchPrices, 'priceId');

    const refresh = () => {
        loadFunds();
        runs.reload();
        prices.reload();
    };
    // Live screens (docs/live-sync/implementation-plan.md 4.6): re-read, exactly as Refresh does, when
    // a price file is checked (NAV runs to approve), or a run is overridden, approved or rejected.
    useLiveTopic(['prices'], refresh);

    const decided = () => {
        refresh();
        onRunsChanged?.();
    };

    const fundOptions = funds.map((f) => ({ value: f.fundId, label: f.fundId + ' · ' + (f.name ?? f.fundId) }));
    const fundNames = Object.fromEntries(funds.map((f) => [f.fundId, f.name ?? f.shortName ?? null]));

    return (
        <>
            <section className="panel unit-prices-panel">
                <div className="unit-prices-toolbar">
                    <div className="unit-prices-heading">
                        <h3 className="panel-title">Current unit prices</h3>
                        <InfoBanner>
                            The latest published price of each active fund. A NAV file uploaded on File Uploads makes a NAV run for
                            each fund in it once its scan passes; the price is published when another person approves the run, with
                            a code from their authenticator app <SafetyOutlined />. Last refreshed{' '}
                            {refreshedAt ? refreshedAt.toLocaleTimeString('en-GB', { timeZone: 'Asia/Colombo' }) : '—'}.
                        </InfoBanner>
                    </div>
                    <Button className="refresh-button" icon={<ActionIcon name="refresh" />} onClick={refresh}
                        loading={fundsLoading || runs.loading || prices.loading}>Refresh</Button>
                </div>
                {fundsError && <Alert type="error" showIcon message={fundsError} className="auth-alert" closable onClose={() => setFundsError(null)} />}
                <PriceCards cards={priceCards(funds)} />
            </section>

            <section className="panel unit-prices-panel">
                <div className="unit-prices-heading">
                    <h3 className="panel-title">{runFile?.fileName ?? 'NAV runs'}</h3>
                    <InfoBanner>
                        {runFile ? 'The NAV runs from the last file uploaded' + (runFile.businessDate ? ', for ' + runFile.businessDate : '') + '. ' : ''}
                        Select a run to see its checks, the file it came from and its trail, and to decide it.
                    </InfoBanner>
                </div>
                {runs.error && <Alert type="error" showIcon message={runs.error} className="auth-alert" closable onClose={runs.clearError} />}
                <RunsTable runs={runs.items} fundNames={fundNames} loading={runs.loading} onOpen={(id) => setOpenRun({ navRunId: id, intent: null })}
                    onDecide={(id, intent) => setOpenRun({ navRunId: id, intent })} />
                {runs.nextCursor && <div className="register-list-footer"><Button onClick={runs.loadMore} loading={runs.loadingMore}>Load more</Button></div>}
            </section>

            <section className="panel unit-prices-panel">
                <div className="unit-prices-heading">
                    <h3 className="panel-title">Price History</h3>
                    <InfoBanner>Every published price, newest first. Imported history came from an initial load and never prices a transaction.</InfoBanner>
                </div>
                <div className="unit-prices-filters">
                    <Select aria-label="Fund" allowClear placeholder="Every Fund" className="unit-prices-fund-filter" value={priceFund ?? undefined}
                        onChange={(v) => setPriceFund(v ?? null)} options={fundOptions} />
                    <RangePicker aria-label="Effective dates" value={priceRange} format="YYYY-MM-DD" allowEmpty={[true, true]} onChange={(r) => setPriceRange(r ?? null)} />
                    <Checkbox checked={superseded} onChange={(e) => setSuperseded(e.target.checked)}>Show superseded versions</Checkbox>
                </div>
                {prices.error && <Alert type="error" showIcon message={prices.error} className="auth-alert" closable onClose={prices.clearError} />}
                <PricesTable prices={prices.items} loading={prices.loading} />
                {prices.nextCursor && <div className="register-list-footer"><Button onClick={prices.loadMore} loading={prices.loadingMore}>Load more</Button></div>}
            </section>

            <RunDrawer navRunId={openRun?.navRunId ?? null} intent={openRun?.intent ?? null} onClose={() => setOpenRun(null)}
                withStepUp={withStepUp} onDecided={decided} />
            {stepUpDialog}
        </>
    );
};

export default UnitPrices;
